import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../client.js';

export type MachbarkeitTech = 'einfach' | 'herausfordernd' | 'sehr_anspruchsvoll' | '';
export type MachbarkeitWirt = 'positiv' | 'neutral' | 'risikoreich' | '';
export type ZeitenFlexibility = 'hart' | 'flexibel' | 'offen' | '';

export interface OrgLayoutNode { id: string; x: number; y: number }
export interface OrgLayoutEdge { id: string; source: string; target: string }
export interface TeamOrgLayout {
  nodes: OrgLayoutNode[];
  edges: OrgLayoutEdge[];
}

export const EMPTY_ORG_LAYOUT: TeamOrgLayout = { nodes: [], edges: [] };

export interface ProjectInitialization {
  // Schritt 1: Machbarkeit
  machbarkeit_problem: string;
  machbarkeit_scope: string;
  machbarkeit_tech: MachbarkeitTech;
  machbarkeit_wirt: MachbarkeitWirt;
  machbarkeit_out_of_scope: string;
  // Schritt 2: Rahmen
  rahmen_vorgaben: string;
  rahmen_annahmen: string;
  rahmen_sonstige: string;
  // Schritt 3: Projektauftrag (zusaetzlich zu magic_triangle / stacey aus project_config)
  projektauftrag_auftraggeber: string;
  projektauftrag_sponsor: string;
  projektauftrag_vertrag: string;
  projektauftrag_rahmenbedingungen: string;
  // Schritt 4: Ziele
  ziele_haupt: string;
  ziele_teil: string;
  ziele_nicht: string;
  ziele_akzeptanz: string;
  // Schritt 6: Team — Externe Beteiligte
  team_kunde: string;
  team_lieferant: string;
  // Schritt 6: Organigramm-Layout (Frontend-Snapshot, Backend speichert JSONB opaque)
  team_org_layout: TeamOrgLayout;
  // Schritt 5: Zeiten / Budget
  zeiten_start_date: string | null;
  zeiten_end_date: string | null;
  zeiten_flexibility: ZeitenFlexibility;
  zeiten_aufwand_pt: number | null;
  // Freitext (Backend-Spalte wurde von numeric auf text umgestellt, P-05).
  zeiten_budget: string | null;
  zeiten_budget_flex: ZeitenFlexibility;
  zeiten_pflichttermine: string;
  zeiten_beschraenkungen: string;
}

const DEFAULT_DATA: ProjectInitialization = {
  machbarkeit_problem: '',
  machbarkeit_scope: '',
  machbarkeit_tech: '',
  machbarkeit_wirt: '',
  machbarkeit_out_of_scope: '',
  rahmen_vorgaben: '',
  rahmen_annahmen: '',
  rahmen_sonstige: '',
  projektauftrag_auftraggeber: '',
  projektauftrag_sponsor: '',
  projektauftrag_vertrag: '',
  projektauftrag_rahmenbedingungen: '',
  team_kunde: '',
  team_lieferant: '',
  team_org_layout: EMPTY_ORG_LAYOUT,
  ziele_haupt: '',
  ziele_teil: '',
  ziele_nicht: '',
  ziele_akzeptanz: '',
  zeiten_start_date: null,
  zeiten_end_date: null,
  zeiten_flexibility: 'flexibel',
  zeiten_aufwand_pt: null,
  zeiten_budget: null,
  zeiten_budget_flex: 'flexibel',
  zeiten_pflichttermine: '',
  zeiten_beschraenkungen: '',
};

export interface UseProjectInitializationResult {
  data: ProjectInitialization;
  isLoading: boolean;
  save: (patch: Partial<ProjectInitialization>) => Promise<void>;
}

/**
 * Server liefert Lazy-Init: alle Felder können null sein. Wir mappen fehlende
 * Felder auf die DEFAULT-Werte, damit die Tabs unverändert mit string-Defaults
 * arbeiten können (kein null in Textfeldern).
 */
function normalizeOrgLayout(raw: unknown): TeamOrgLayout {
  if (!raw || typeof raw !== 'object') return EMPTY_ORG_LAYOUT;
  const r = raw as { nodes?: unknown; edges?: unknown };
  const nodes = Array.isArray(r.nodes)
    ? r.nodes.flatMap((n) => {
        const node = n as { id?: unknown; x?: unknown; y?: unknown };
        if (typeof node.id !== 'string' || typeof node.x !== 'number' || typeof node.y !== 'number') return [];
        return [{ id: node.id, x: node.x, y: node.y }];
      })
    : [];
  const edges = Array.isArray(r.edges)
    ? r.edges.flatMap((e) => {
        const edge = e as { id?: unknown; source?: unknown; target?: unknown };
        if (typeof edge.source !== 'string' || typeof edge.target !== 'string') return [];
        return [{ id: typeof edge.id === 'string' ? edge.id : `${edge.source}-${edge.target}`, source: edge.source, target: edge.target }];
      })
    : [];
  return { nodes, edges };
}

function normalize(raw: unknown): ProjectInitialization {
  const envelope = (raw ?? {}) as { data?: unknown };
  const inner = (envelope.data ?? raw ?? {}) as Partial<Record<keyof ProjectInitialization, unknown>>;
  const r = inner;
  return {
    machbarkeit_problem: (r.machbarkeit_problem as string) ?? '',
    machbarkeit_scope: (r.machbarkeit_scope as string) ?? '',
    machbarkeit_tech: (r.machbarkeit_tech as MachbarkeitTech) ?? '',
    machbarkeit_wirt: (r.machbarkeit_wirt as MachbarkeitWirt) ?? '',
    machbarkeit_out_of_scope: (r.machbarkeit_out_of_scope as string) ?? '',
    rahmen_vorgaben: (r.rahmen_vorgaben as string) ?? '',
    rahmen_annahmen: (r.rahmen_annahmen as string) ?? '',
    rahmen_sonstige: (r.rahmen_sonstige as string) ?? '',
    projektauftrag_auftraggeber: (r.projektauftrag_auftraggeber as string) ?? '',
    projektauftrag_sponsor: (r.projektauftrag_sponsor as string) ?? '',
    projektauftrag_vertrag: (r.projektauftrag_vertrag as string) ?? '',
    projektauftrag_rahmenbedingungen: (r.projektauftrag_rahmenbedingungen as string) ?? '',
    team_kunde: (r.team_kunde as string) ?? '',
    team_lieferant: (r.team_lieferant as string) ?? '',
    team_org_layout: normalizeOrgLayout(r.team_org_layout),
    ziele_haupt: (r.ziele_haupt as string) ?? '',
    ziele_teil: (r.ziele_teil as string) ?? '',
    ziele_nicht: (r.ziele_nicht as string) ?? '',
    ziele_akzeptanz: (r.ziele_akzeptanz as string) ?? '',
    zeiten_start_date: (r.zeiten_start_date as string | null) ?? null,
    zeiten_end_date: (r.zeiten_end_date as string | null) ?? null,
    zeiten_flexibility: (r.zeiten_flexibility as ZeitenFlexibility) || 'flexibel',
    zeiten_aufwand_pt: (r.zeiten_aufwand_pt as number | null) ?? null,
    zeiten_budget: (r.zeiten_budget as string | null) ?? null,
    zeiten_budget_flex: (r.zeiten_budget_flex as ZeitenFlexibility) || 'flexibel',
    zeiten_pflichttermine: (r.zeiten_pflichttermine as string) ?? '',
    zeiten_beschraenkungen: (r.zeiten_beschraenkungen as string) ?? '',
  };
}

const KEY = (pid: string) => ['projects', pid, 'initialization'] as const;

export function useProjectInitialization(
  projectId: string | undefined,
): UseProjectInitializationResult {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: KEY(projectId ?? ''),
    queryFn: async () => {
      const raw = await apiClient.get(`/api/v1/projects/${projectId}/initialization`);
      return normalize(raw);
    },
    enabled: !!projectId,
  });

  const mutation = useMutation({
    mutationFn: (patch: Partial<ProjectInitialization>) =>
      apiClient.patch(`/api/v1/projects/${projectId}/initialization`, patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY(projectId ?? '') });
    },
  });

  return {
    data: query.data ?? DEFAULT_DATA,
    isLoading: query.isLoading,
    save: async (patch: Partial<ProjectInitialization>) => {
      await mutation.mutateAsync(patch);
    },
  };
}
